import React, { useState, useEffect, useRef } from 'react';
import emailjs from '@emailjs/browser';
import SEO from '../components/SEO';

function ContactPage() {
    const [formData, setFormData] = useState({
        name: '',
        phone: '',
        email: '',
        message: ''
    });


    const GOOGLE_SCRIPT_URL = "https://script.google.com/macros/s/AKfycbyzUOfy4156YsVF8SRSqSaZWHW_epnKIlRCfAbdrDj72LaCMCubEjRF-Cy3wHYBspnSWw/exec";

    // EMAILJS CONFIGURATION - REPLACE THESE WITH YOUR ACTUAL KEYS
    const EMAILJS_SERVICE_ID = "YOUR_SERVICE_ID";
    const EMAILJS_TEMPLATE_ID = "YOUR_TEMPLATE_ID";
    const EMAILJS_PUBLIC_KEY = "YOUR_PUBLIC_KEY";

    const headerRef = useRef(null);
    const formRef = useRef(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        const observerOptions = {
            threshold: 0.1
        };

        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.style.opacity = '1';
                    entry.target.style.transform = 'translateY(0)';
                }
            });
        }, observerOptions);

        if (headerRef.current) {
            headerRef.current.style.opacity = '1';
            headerRef.current.style.transform = 'translateY(0)';
        }

        if (formRef.current) {
            observer.observe(formRef.current);
        }

        return () => observer.disconnect();
    }, []);

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            // Create form data using URLSearchParams which is standard for form submissions
            const formDataToSend = new URLSearchParams();
            formDataToSend.append('timestamp', new Date().toISOString());
            formDataToSend.append('name', formData.name);
            formDataToSend.append('phone', formData.phone);
            formDataToSend.append('email', formData.email);
            formDataToSend.append('message', formData.message);

            console.log("Submitting form data to:", GOOGLE_SCRIPT_URL);
            // 1. Send to Google Sheets
            const googleSheetRequest = fetch(GOOGLE_SCRIPT_URL, {
                method: "POST",
                mode: "no-cors",
                body: formDataToSend
            });

            // 2. Send to EmailJS
            const emailJsRequest = emailjs.send(
                EMAILJS_SERVICE_ID,
                EMAILJS_TEMPLATE_ID,
                {
                    from_name: formData.name,
                    from_email: formData.email,
                    phone: formData.phone,
                    message: formData.message
                },
                EMAILJS_PUBLIC_KEY
            );

            // Wait for both to complete, regardless of success/failure
            const results = await Promise.allSettled([googleSheetRequest, emailJsRequest]);

            // Check if at least one succeeded
            const isAnySuccess = results.some(result => result.status === 'fulfilled');

            if (isAnySuccess) {
                alert('Thank you! Your message has been sent successfully.');
                setFormData({ name: '', phone: '', email: '', message: '' });

                // Optional debug logging
                results.forEach((result, index) => {
                    if (result.status === 'rejected') {
                        console.error(`Submission ${index === 0 ? 'Google Sheets' : 'EmailJS'} failed:`, result.reason);
                    }
                });
            } else {
                throw new Error("Both submission methods failed.");
            }
        } catch (error) {
            console.error("Error submitting form", error);
            alert("There was an error sending your message. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="contact-page">
            <SEO
                title="Contact Us | Rainbow Craft Signs & Graphics LLC | Dubai, UAE"
                description="Contact Rainbow Craft Signs & Graphics LLC in Dubai, UAE. Get a free quote for LED signage, neon signs, 3D lettering, vehicle wrapping, corporate branding, and more. Call +971 50 860 4485 or email sales@rainbowcraft.ae"
                keywords="contact rainbow craft dubai, signage company contact dubai, LED signage quote dubai, neon sign quote dubai, 3D lettering quote dubai, vehicle wrapping quote dubai, corporate branding quote dubai, signage consultation dubai"
                url="/contact"
                schema={{
                    "@context": "https://schema.org",
                    "@type": "ContactPage",
                    "name": "Contact Us - Rainbow Craft Signs & Graphics",
                    "description": "Contact details for Rainbow Craft Signs & Graphics LLC in Abu Dhabi, UAE.",
                    "url": "https://www.rainbowcraft.ae/contact",
                    "mainEntity": {
                        "@type": "LocalBusiness",
                        "@id": "https://www.rainbowcraft.ae",
                        "name": "Rainbow Craft Signs & Graphics LLC",
                        "telephone": "+971569004913",
                        "email": "sales@rainbowcraft.ae",
                        "address": {
                            "@type": "PostalAddress",
                            "streetAddress": "Musaffah",
                            "addressLocality": "Abu Dhabi",
                            "addressRegion": "Abu Dhabi",
                            "addressCountry": "AE"
                        },
                        "geo": {
                            "@type": "GeoCoordinates",
                            "latitude": 24.2992,
                            "longitude": 54.6972
                        },
                        "openingHoursSpecification": {
                            "@type": "OpeningHoursSpecification",
                            "dayOfWeek": [
                                "Monday",
                                "Tuesday",
                                "Wednesday",
                                "Thursday",
                                "Friday",
                                "Saturday"
                            ],
                            "opens": "08:00",
                            "closes": "18:00"
                        },
                        "areaServed": {
                            "@type": "Country",
                            "name": "United Arab Emirates"
                        }
                    }
                }}
            />
            <div className="page-header" ref={headerRef} style={{
                opacity: 0,
                transform: 'translateY(30px)',
                transition: 'opacity 0.8s ease, transform 0.8s ease'
            }}>
                <div className="container">
                    <h1 className="page-title">CONTACT US</h1>
                    <p className="page-subtitle">
                        Get in touch with Rainbow Craft Signs & Graphics LLC. We're here to help bring your
                        signage and branding vision to life.
                    </p>
                </div>
            </div>

            <section className="contact-form-section">
                <div className="container">
                    <div className="contact-layout">
                        <div className="contact-info-column">
                            <h2 className="section-title-new">GET IN TOUCH</h2>
                            <div className="contact-details">
                                <div className="contact-detail-item">
                                    <div className="detail-icon">
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h4>EMAIL</h4>
                                        <p>sales@rainbowcraft.ae</p>
                                    </div>
                                </div>

                                <div className="contact-detail-item">
                                    <div className="detail-icon">
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h4>PHONE</h4>
                                        <p>+971 50 860 4485</p>
                                    </div>
                                </div>

                                <div className="contact-detail-item">
                                    <div className="detail-icon">
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
                                            <circle cx="12" cy="10" r="3" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h4>ADDRESS</h4>
                                        <p>Musaffah, Abu Dhabi, UAE</p>
                                    </div>
                                </div>

                                <div className="contact-detail-item">
                                    <div className="detail-icon">
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <circle cx="12" cy="12" r="10" />
                                            <path d="M12 6v6l4 2" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h4>WORKING HOURS</h4>
                                        <p>Monday - Saturday: 8:00 AM - 6:00 PM</p>
                                        <p>Sunday: Closed</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="contact-form-column" ref={formRef} style={{
                            opacity: 0,
                            transform: 'translateY(50px)',
                            transition: 'opacity 0.6s ease, transform 0.6s ease'
                        }}>
                            <form onSubmit={handleSubmit} className="contact-form">
                                <div className="form-group">
                                    <label htmlFor="name">NAME *</label>
                                    <input
                                        type="text"
                                        id="name"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        required
                                        placeholder="Your full name"
                                    />
                                </div>

                                <div className="form-group">
                                    <label htmlFor="phone">PHONE NUMBER (UAE) *</label>
                                    <input
                                        type="tel"
                                        id="phone"
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleChange}
                                        required
                                        placeholder="+971 XX XXX XXXX"
                                        pattern="[+]?[0-9]{10,15}"
                                    />
                                </div>

                                <div className="form-group">
                                    <label htmlFor="email">EMAIL *</label>
                                    <input
                                        type="email"
                                        id="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        required
                                        placeholder="your.email@example.com"
                                    />
                                </div>

                                <div className="form-group">
                                    <label htmlFor="message">MESSAGE *</label>
                                    <textarea
                                        id="message"
                                        name="message"
                                        value={formData.message}
                                        onChange={handleChange}
                                        required
                                        rows="6"
                                        placeholder="Tell us about your project..."
                                    ></textarea>
                                </div>

                                <button type="submit" className="btn-primary submit-btn" disabled={isSubmitting}>
                                    {isSubmitting ? 'SENDING...' : 'SEND MESSAGE'}
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}

export default ContactPage;
